import { Injectable, inject } from "@angular/core";
import { MaterialCssVarsService } from "@app/app-libs/material-css-vars/material-css-vars.service";
import { HueValue } from "@app/app-libs/material-css-vars/material-css-vars.model";
import { Field } from "@app/shared/models/form-field.model";
import { ThemeManagerService } from "@app/portals/admin-modules/theme-manager/theme-manager.service";
import { ThemeManager } from "@app/portals/admin-modules/theme-manager/theme-manager.model";
import { ICON_FONT_SET } from "@app/shared/constants/icons.constants";
import { BehaviorSubject } from "rxjs";
import { ThemeSettings } from "@app/shared/models/user-settings.model";
import { UserSettingsService } from "@app/core/services/user-settings.service";

@Injectable({
  providedIn: "root"
})
export class ThemeService {
  private userSettingsService = inject(UserSettingsService);
  private materialCssVarsService = inject(MaterialCssVarsService);
  private themeManagerService = inject(ThemeManagerService);
  private _iconAppearance: BehaviorSubject<string> = new BehaviorSubject<string>(ICON_FONT_SET.FA_DEFAULT);
  private _ignoreSolidAppearance: BehaviorSubject<string> = new BehaviorSubject<string>(ICON_FONT_SET.FA_DEFAULT);

  /**
   * Get theme settings
   */
  getTheme(): ThemeSettings {
    return {
      primaryColor: this.materialCssVarsService.primary,
      accentColor: this.materialCssVarsService.accent,
      warningColor: this.materialCssVarsService.warn,
      isDarkTheme: this.materialCssVarsService.isDarkTheme,
      isAutoContrast: this.materialCssVarsService.isAutoContrast,
      contrastColorThresholdPrimary: this.materialCssVarsService.contrastColorThresholdPrimary,
      contrastColorThresholdAccent: this.materialCssVarsService.contrastColorThresholdAccent,
      contrastColorThresholdWarn: this.materialCssVarsService.contrastColorThresholdWarn,
      iconAppearance: this._iconAppearance.value,
      colorWorkspaceLight: this.materialCssVarsService.lightWorkspace,
      colorWorkspaceDark: this.materialCssVarsService.darkWorkspace,
      colorPrimaryButton: ""
    };
  }

  /**
   * Update theme and optionally save to storage
   * @param theme Theme setting to update to
   * @param preview Preview only or save to storage?
   */
  setTheme(theme: string, preview: boolean = false): void {
    const themeManager = new Object() as ThemeManager;
    themeManager.themeId = new Object() as Field;
    themeManager.themeId.value = theme;

    this.themeManagerService.fetchThemeById(themeManager).subscribe((results) => {
      if (results) {
        const themeSettings: ThemeSettings = {
          primaryColor: results.primaryColor.value as HueValue,
          accentColor: results.accentColor.value as HueValue,
          warningColor: results.warningColor.value as HueValue,
          isDarkTheme: results.isDarkTheme.value as boolean,
          isAutoContrast: results.isAutoContrast.value as boolean,
          contrastColorThresholdPrimary: results.contrastColorThresholdPrimary.value as HueValue,
          contrastColorThresholdAccent: results.contrastColorThresholdAccent.value as HueValue,
          contrastColorThresholdWarn: results.contrastColorThresholdWarn.value as HueValue,
          iconAppearance: this._iconAppearance.value,
          colorWorkspaceLight: results.colorWorkspaceLight.value as HueValue,
          colorWorkspaceDark: results.colorWorkspaceDark.value as HueValue,
          colorPrimaryButton: ""
        };
        this.setThemeAttributes(themeSettings, preview);
      }
    });
  }

  /**
   * Update theme and optionally save to storage
   * @param themeSetting
   * @param preview
   */
  setThemeAttributes(themeSetting: ThemeSettings, preview: boolean = false) {
    if (!themeSetting) {
      return;
    }

    this.materialCssVarsService.setPrimaryColor(themeSetting.primaryColor);
    this.materialCssVarsService.setAccentColor(themeSetting.accentColor);
    this.materialCssVarsService.setWarnColor(themeSetting.warningColor);
    this.materialCssVarsService.setLightWorkspace(themeSetting.colorWorkspaceLight);
    this.materialCssVarsService.setDarkWorkspace(themeSetting.colorWorkspaceDark);
    // Check for valid theme before setting auto contrast.
    if (themeSetting.primaryColor) {
      this.materialCssVarsService.setAutoContrastEnabled(themeSetting.isAutoContrast);
    }

    if (!themeSetting.isAutoContrast) {
      this.materialCssVarsService.setContrastColorThresholdPrimary(themeSetting.contrastColorThresholdPrimary);
      this.materialCssVarsService.setContrastColorThresholdPrimary(themeSetting.contrastColorThresholdAccent);
      this.materialCssVarsService.setContrastColorThresholdPrimary(themeSetting.contrastColorThresholdWarn);
    }

    this.materialCssVarsService.setDarkTheme(themeSetting.isDarkTheme);
    this.setIconAppearance(themeSetting.iconAppearance);

    if (!preview) {
      this.userSettingsService.theme.iconAppearance = this._iconAppearance.value;
      this.userSettingsService.theme = themeSetting;
    }
  }

  /**
   * Update icon appearance and optionally save to storage
   * If no value is provided, the default will be set.
   * @param icon
   */
  setIconAppearance(icon: string) {
    this._iconAppearance.next(icon || this.defaultIconAppearance);
  }

  /**
   * Return user defined icon appearance
   */
  userDefinedIconAppearance = (ignoreSolid?: boolean, checkAppearance?: boolean): BehaviorSubject<string> => {
    if (ignoreSolid) {
      this._ignoreSolidAppearance.next(this._checkSolid(checkAppearance));
      return this._ignoreSolidAppearance;
    }

    return this._iconAppearance;
  };

  get isSolid() {
    return [ICON_FONT_SET.FA_SOLID, ICON_FONT_SET.FA_DUOTONE].includes(this._iconAppearance.value);
  }

  /**
   * Return user defined icon appearance
   */
  get defaultIconAppearance() {
    return ICON_FONT_SET.FA_DEFAULT;
  }

  private _checkSolid(check: boolean) {
    if (check) {
      return this.isSolid ? this._iconAppearance.value : ICON_FONT_SET.FA_SOLID;
    } else {
      return this.isSolid ? ICON_FONT_SET.FA_LIGHT : this._iconAppearance.value;
    }
  }
}
